
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { validateReferralCode } from '@/utils/referralUtils';
import { validateOAuthState, verifyOAuthOrigin } from '@/utils/oauth-security';
import { categorizeOAuthError } from '@/utils/oauth-errors';
import { userAssignmentService } from '@/services/userAssignmentService';
import { EmailConfirmationService } from '@/services/emailConfirmationService';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toastShown, setToastShown] = useState(false);

  const showSuccessToast = () => {
    if (!toastShown) {
      toast.success("Authentication successful");
      setToastShown(true);
    }
  };

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("=== PROCESSING AUTH CALLBACK WITH EMAIL CONFIRMATION SUPPORT ===");
        console.log("Current URL:", window.location.href);
        
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const errorParam = searchParams.get('error');
        const tokensParam = searchParams.get('tokens');
        const stateParam = searchParams.get('state');
        
        let stateValidation: { isValid: boolean; data?: any; error?: string } | null = null;
        
        // Enhanced cTrader OAuth security validation
        if ((tokensParam || code) && stateParam) {
          console.log("=== ENHANCED CTRADER OAUTH SECURITY VALIDATION ===");
          
          stateValidation = validateOAuthState(stateParam);
          if (!stateValidation.isValid) {
            console.error("OAuth state validation failed:", stateValidation.error);
            const categorizedError = categorizeOAuthError({ message: stateValidation.error });
            
            const errorMessage = `Security validation failed. Please try connecting again.`;
            toast.error(errorMessage);
            
            localStorage.setItem('ctrader_oauth_result', JSON.stringify({
              type: 'ctrader-oauth-error',
              error: errorMessage,
              details: stateValidation.error
            }));
            
            const returnPath = localStorage.getItem('ctrader_return_path') || '/exchange-accounts';
            navigate(returnPath);
            return;
          }
          
          const expectedOrigin = window.location.origin;
          const actualOrigin = stateValidation.data?.appOrigin;
          
          if (!verifyOAuthOrigin(expectedOrigin, actualOrigin)) {
            console.warn("OAuth origin verification failed - proceeding with caution");
            toast("Security notice: Origin verification failed, but proceeding with authentication", {
              duration: 3000
            });
          }
          
          console.log("✅ OAuth security validation passed");
        }
        
        // Handle cTrader OAuth tokens
        if (tokensParam) {
          try {
            const tokens = JSON.parse(decodeURIComponent(tokensParam));
            console.log("Found cTrader tokens in URL params");
            
            localStorage.setItem('ctrader_oauth_result', JSON.stringify({
              type: 'ctrader-oauth-tokens',
              tokens: tokens,
            }));
            
            const url = new URL(window.location.href);
            url.searchParams.delete('tokens');
            url.searchParams.delete('state');
            window.history.replaceState({}, document.title, url.toString());
            
            let returnPath = stateValidation?.data?.returnPath || localStorage.getItem('ctrader_return_path');
            
            if (!returnPath) {
              returnPath = '/exchange-accounts';
            } else if (returnPath.includes('/ctrader-profiles/')) {
              console.log('Staying on cTrader profile page:', returnPath);
            } else {
              returnPath = '/exchange-accounts';
            }
            
            const separator = returnPath.includes('?') ? '&' : '?';
            const finalPath = returnPath + separator + 'openConnectDialog=true';
            
            showSuccessToast();
            navigate(finalPath);
            return;
          } catch (e) {
            console.error('Failed to parse tokens from URL:', e);
            const categorizedError = categorizeOAuthError(e);
            setError(categorizedError.userMessage);
            toast.error(categorizedError.userMessage);
          }
        }
        
        // Handle cTrader OAuth code exchange
        if (code && !errorParam) {
          console.log("Processing cTrader authorization code");
          setMessage("Processing authorization code...");
          
          try {
            const adminResponse = await fetch(
              'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/get-admin-credentials',
              {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
              }
            );
            
            if (!adminResponse.ok) {
              throw new Error('Failed to get admin credentials');
            }
            
            const adminData = await adminResponse.json();
            const redirectUri = `${window.location.origin}/auth/callback`;
            
            const tokenResponse = await fetch(
              'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/ctrader-callback',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  code,
                  client_id: adminData.client_id,
                  client_secret: 'admin-managed', 
                  redirect_uri: redirectUri
                })
              }
            );
            
            if (!tokenResponse.ok) {
              const errorText = await tokenResponse.text();
              throw new Error(`Token exchange failed: ${errorText}`);
            }
            
            const tokens = await tokenResponse.json();
            
            localStorage.setItem('ctrader_oauth_result', JSON.stringify({
              type: 'ctrader-oauth-tokens',
              tokens
            }));
            
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, document.title, url.toString());
            
            let returnPath = stateValidation?.data?.returnPath || localStorage.getItem('ctrader_return_path');
            
            if (!returnPath) {
              returnPath = '/exchange-accounts';
            } else if (returnPath.includes('/ctrader-profiles/')) {
              console.log('Code exchange - staying on cTrader profile page:', returnPath);
            } else {
              returnPath = '/exchange-accounts';
            }
            
            const separator = returnPath.includes('?') ? '&' : '?';
            const finalPath = returnPath + separator + 'openConnectDialog=true';
            
            showSuccessToast();
            navigate(finalPath);
            return;
          } catch (e) {
            console.error('Error exchanging code for tokens:', e);
            const categorizedError = categorizeOAuthError(e);
            setError(categorizedError.userMessage);
            toast.error(categorizedError.userMessage);
            
            const returnPath = stateValidation?.data?.returnPath || localStorage.getItem('ctrader_return_path') || '/exchange-accounts';
            navigate(returnPath);
            return;
          }
        }
        
        // Handle cTrader OAuth error
        if (errorParam) {
          console.error("OAuth error:", errorParam);
          const categorizedError = categorizeOAuthError({ message: errorParam });
          setError(categorizedError.userMessage);
          
          toast.error(`${categorizedError.userMessage}. Please try connecting again.`, {
            duration: 5000
          });
          
          localStorage.setItem('ctrader_oauth_result', JSON.stringify({
            type: 'ctrader-oauth-error', 
            error: errorParam
          }));
          
          const returnPath = stateValidation?.data?.returnPath || localStorage.getItem('ctrader_return_path') || '/exchange-accounts';
          navigate(returnPath);
          return;
        }

        // Handle regular Supabase auth flow with email confirmation support
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const isOAuthCallback = accessToken || (code && !tokensParam);
        
        let session = null;
        
        if (accessToken) {
          console.log("Found access token in URL hash, getting session");
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Error getting session:", error);
            throw error;
          }
          
          session = data.session;
        } else if (isOAuthCallback) {
          console.log("No access token in URL, exchanging code for session");
          try {
            const { data, error } = await supabase.auth.exchangeCodeForSession(
              window.location.search
            );

            if (error) {
              console.error("Error exchanging code for session:", error);
              throw error;
            }

            session = data.session;
          } catch (exchangeError) {
            console.error("Failed to exchange code for session:", exchangeError);
            throw exchangeError;
          }
        }

        // Process pending referral data from database after email confirmation
        if (session?.user && isOAuthCallback) {
          try {
            console.log("🔍 [EMAIL_CONFIRMATION] Checking for pending referral data...");
            
            const pendingData = await EmailConfirmationService.getPendingReferralData(session.user.id);
            
            if (pendingData.needsProcessing && pendingData.referralCode && pendingData.affiliateId) {
              console.log("📋 [EMAIL_CONFIRMATION] Found pending referral data:", {
                referralCode: pendingData.referralCode,
                affiliateId: pendingData.affiliateId
              });
              
              const referralData = await validateReferralCode(pendingData.referralCode);
              
              if (referralData.isValid && referralData.affiliateId === pendingData.affiliateId) {
                console.log("✅ [EMAIL_CONFIRMATION] Pending referral code is valid, processing assignment...");
                
                const assignmentSuccess = await userAssignmentService.assignUserToAffiliateOnRegistration(
                  session.user.id,
                  pendingData.affiliateId,
                  pendingData.referralCode
                );
                
                if (assignmentSuccess) {
                  console.log("✅ [EMAIL_CONFIRMATION] Successfully processed pending referral assignment");
                  
                  await EmailConfirmationService.markReferralAsProcessed(session.user.id);
                  
                  toast.success("Welcome! Your account has been confirmed and you've been successfully assigned to your referrer's affiliate program.");
                } else {
                  console.error("❌ [EMAIL_CONFIRMATION] Failed to process pending referral assignment");
                  toast.error("Account confirmed, but referral assignment failed. Please contact support.");
                }
              } else {
                console.warn("⚠️ [EMAIL_CONFIRMATION] Pending referral code is no longer valid");
                
                await EmailConfirmationService.markReferralAsProcessed(session.user.id);
                
                toast.warning("Account confirmed, but referral code is no longer valid.");
              }
            } else {
              console.log("ℹ️ [EMAIL_CONFIRMATION] No pending referral data found");
            }
            
            // Also check localStorage for OAuth flows
            const pendingRefCode = localStorage.getItem('pendingReferralCode');
            if (pendingRefCode && !pendingData.needsProcessing) {
              console.log("🔍 [OAUTH] Processing referral code from localStorage:", pendingRefCode);
              
              try {
                const referralData = await validateReferralCode(pendingRefCode);
                
                if (referralData.isValid) {
                  console.log("✅ [OAUTH] Valid referral code for OAuth, starting assignment");
                  
                  const assignmentSuccess = await userAssignmentService.assignUserToAffiliate(
                    session.user.id,
                    referralData.affiliateId,
                    pendingRefCode
                  );
                  
                  if (assignmentSuccess) {
                    console.log("✅ [OAUTH] OAuth user successfully assigned to affiliate system");
                    toast.success("Welcome! You've been successfully registered with your referral code.");
                  } else {
                    console.error("❌ [OAUTH] Failed to assign OAuth user to affiliate system");
                    toast.error("Registration successful, but referral assignment failed. Please contact support.");
                  }
                } else {
                  console.warn("⚠️ [OAUTH] Invalid referral code for OAuth:", pendingRefCode);
                  toast.warning("Registration successful, but referral code was invalid.");
                }
                
                localStorage.removeItem('pendingReferralCode');
              } catch (refError) {
                console.error('❌ [OAUTH] Error processing referral code for OAuth:', refError);
                toast.error("Registration successful, but referral processing failed.");
              }
            }
          } catch (error) {
            console.error('❌ [EMAIL_CONFIRMATION] Error processing pending referral data:', error);
          }
        }

        if (session) {
          console.log("Authentication successful, redirecting to home page");
          toast.success("Successfully logged in!");
          navigate('/');
        } else if (isOAuthCallback) {
          throw new Error('No session found');
        } else {
          console.log("No session but not OAuth callback, redirecting to login");
          navigate('/auth/login');
        }
        
      } catch (error: any) {
        console.error('Auth callback error:', error);
        const categorizedError = categorizeOAuthError(error);
        setError(categorizedError.userMessage);
        toast.error(categorizedError.userMessage);
        navigate('/auth/login');
      }
    };

    handleAuthCallback();
  }, [navigate, location]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">Processing login...</h2>
        <p className="text-muted-foreground mb-4">
          {message || "Please wait while we complete your authentication."}
        </p>
        {error ? (
          <div className="text-red-500 mt-2">{error}</div>
        ) : (
          <Loader2 className="animate-spin h-8 w-8 mx-auto text-primary" />
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
